import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Modal from '@common/modal';
import Icon from '@common/assets';
import PageCloseModal from '@components/phs/test/pageCloseModal';
import SkillKeyword from '@components/phs/test/keywordModal/SkillKeyword';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { ProfileSkillDto } from '@domain/rs/phs/center/ProfileSkillDto';
import Colors from '@domain/constant/colors';
import { RouteUrl } from '@utils/routeUtil';
import classnames from 'classnames/bind';
import styles from './keywordModal.module.scss';
import { usePhsCenterSkill, usePutPhsCenterSkill } from '@repository/phs/center/usePhsCenter';

const cx = classnames.bind(styles);

interface KeywordModalProps {
  visible: boolean;
  onClose: () => void;
}

const KeywordModal = ({ visible, onClose }: KeywordModalProps) => {
  const { data: userData } = useUserInfo();
  const isLogin = !!userData?.id;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [selectedSkills, setSelectedSkills] = useState<ProfileSkillDto[]>([]);
  const [modalVisible, setModalVisible] = useState(false);

  const { data: skillData, refetch } = usePhsCenterSkill(isLogin);
  const { mutateAsync: saveSkillKeyword } = usePutPhsCenterSkill();

  const handleSave = async () => {
    await saveSkillKeyword({ skills: selectedSkills });
    await refetch();
    onClose();
  };

  useEffect(() => {
    if (skillData?.skills) setSelectedSkills(skillData.skills);
  }, [visible]);

  const getIsSameArray = () => {
    const a = (selectedSkills?.map((skill) => skill.code) || []).sort();
    const b = (skillData?.skills.map((skill) => skill.code) || []).sort();
    return a.length === b.length && a.every((value, index) => value === b[index]);
  };

  const handleClose = () => {
    if (!getIsSameArray()) {
      setModalVisible(true);
      return;
    }

    onClose();
  };

  return (
    <>
      <Modal visible={visible}>
        <div className={cx('layer')}>
          <a className={cx('btnClose')} role="button" onClick={handleClose}>
            <Icon name="closeLight" width={24} height={24} fill={Colors.C_COOL_GRAY_90} />
          </a>
          <div className={cx('titleArea')}>
            <em className={cx('title')}>보유하고 있는 지식 · 기술을 입력해 주세요.</em>
            <div className={cx('descArea')}>
              <Icon name="informationLight" width={20} height={20} fill={Colors.C_COOL_GRAY_70} />
              <p className={cx('desc')}>
                <Link href={RouteUrl.PROFILE} legacyBehavior>
                  <a className={cx('link')} target="_blank" role="button">
                    내 프로필
                  </a>
                </Link>
                에 입력한 기술 키워드와 연동됩니다.
              </p>
            </div>
          </div>

          <div className={cx('scrollArea')}>
            <div ref={scrollRef} className={cx('scrollInner')}>
              <div className={cx('scrollWrap')}>
                <SkillKeyword
                  scrollRef={scrollRef}
                  selectedSkills={selectedSkills}
                  setSelectedSkills={setSelectedSkills}
                />
              </div>
            </div>
          </div>

          <div className={cx('btnArea')}>
            <a className={cx('btnSave')} role="button" onClick={handleSave}>
              저장
            </a>
          </div>
        </div>
      </Modal>

      {/* 페이지 닫기 확인 모달 */}
      <PageCloseModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onConfirm={() => {
          setModalVisible(false);
          onClose();
        }}
      />
    </>
  );
};

export default KeywordModal;
